import { timestampExtension } from "@msgpack/msgpack/dist/timestamp";

function isWhiteSpace(c: string) {
    return c && (c === " " || c === "\n" || c === "\t" || c === "\b" || c.charCodeAt(0) < 32);
}

function isIdentifierChar(c: string) {
    return c && (c === "_" || c.charCodeAt(0) >= 97 && c.charCodeAt(0) <= 122 || c.charCodeAt(0) >= 65 && c.charCodeAt(0) <= 90);
}

function isIdentifierCharInner(c: string) {
    return c && (c === "_" || c.charCodeAt(0) >= 97 && c.charCodeAt(0) <= 122 || c.charCodeAt(0) >= 65 && c.charCodeAt(0) <= 90 || c.charCodeAt(0) >= 48 && c.charCodeAt(0) <= 57);
}

function isNumber(c: string) {
    return c && (c === "." ||  c.charCodeAt(0) >= 48 && c.charCodeAt(0) <= 57);
}

export class CParserError extends Error {
    constructor(
        message: string,
        readonly line: number,
        readonly column: number
    ) {
        super(message);
    }
}

export class CParser {
    private position: number = -1;
    private line: number = 1;
    private column: number = 0;
    private text: string = "";
    private data!: CParserData;

    parse(text: string) {
        this.position = -1;
        this.line = 1;
        this.column = 0;
        this.text = text;
        this.data = new CParserData();

        while (this.position < this.text.length) {
            this.next();
        }

        return this.data;
    }

    private next() {
        let token;

        do {
            if (this.peekToken() === "}") {
                return;
            }

            token = this.readToken();

            if (!token) {
                return;
            }

            if (token === "typedef") {
                this.parseTypeDef();

                this.expect(";");
                continue;
            }

            if (token === "struct") {
                const type = this.parseStruct();

                if (type.name) {
                    this.data.addType(type.name, type);
                }

                this.expect(";");
                continue;
            }

            if (token === "union") {
                const type = this.parseUnion();

                if (type.name) {
                    this.data.addType(type.name, type);
                }

                const token = this.readToken();

                if (token !== ";") {

                }

                this.expect(";");
                continue;
            }

            if (token === "#") {
                while (token && token !== "\n") {
                    token = this.readToken(true);
                }
                continue;
            }

            if (token === "extern") {
                token = this.readToken();

                if (token === "\"") {
                    this.expect("C");
                    this.expect("\"");
                    this.expect("{");
                    this.next();
                    this.expect("}");
                }
                else {
                    this.expect();
                    this.expect(";");
                }
                continue;
            }

            throw new CParserError(`Unknown syntax '${token}'`, this.line, this.column);
        }
        while(token);
    }

    private parseTypeDef() {
        let token = this.readToken();
        let volatile = false;
        let type;
        let name;

        while (token) {
            if (token === "volatile") {
                volatile = true;
            }

            if (!type) {
                if (token === "struct") {
                    type = this.parseStruct();
                }
                else if (token === "union") {
                    type = this.parseUnion();
                }
                else {
                    type = this.data.getType(token);
                }
            }
            else {
                name = token;

                this.data.addType(name, type);
                return;
            }

            token = this.readToken();
        }
    }

    private parseStruct() {
        let name = this.peekToken();

        if (name === "{") {
            name = "";
        }
        else {
            name = this.readToken();
        }

        const struct = new CStruct(name ?? "");

        this.expect("{");

        while (true) {
            if (this.peekToken() === "}") {
                break;
            }

            let type = this.parseType(this.expect());
            const fieldName = this.expect();

            if (fieldName === ";") {
                continue;
            }

            const bracketOrSemicolonOrColon = this.oneOf("[", ";", ":");

            if (bracketOrSemicolonOrColon === "[") {
                // Array
                const length = this.expect();

                type = type.makeArrayType(parseInt(length));

                this.expect("]");
                this.expect(";");
            }
            else if (bracketOrSemicolonOrColon === ":") {
                // Bit field

                const bits = this.expect();

                if (!(type instanceof CTypeFixed)) {
                    throw new CParserError("Invalid bit type", this.line, this.column);
                }

                type = type.makeBitFieldType(parseInt(bits));

                this.expect(";");
            }
            else if (bracketOrSemicolonOrColon === ";") {
                // Next field
            }

            struct.addField(type, fieldName);

            const next = this.peekToken();

            if (next === "}") {
                break;
            }
        }

        this.expect("}");

        return struct;
    }

    private parseUnion() {
        let name = this.peekToken();

        if (name === "{") {
            name = "";
        }
        else {
            name = this.readToken();
        }

        const union = new CUnion(name ?? "");

        this.expect("{");

        while (true) {
            if (this.peekToken() === "}") {
                break;
            }

            const type = this.parseType(this.expect());
            const fieldName = this.expect();

            if (fieldName === ";") {
                if (!(type instanceof CStruct)) {
                    throw new CParserError("Invalid", this.line, this.column);
                }

                if (!type.name) {
                    union.addField(type, "(default)");
                }

                continue;
            }


            const bracketOrSemicolonOrColon = this.oneOf("[", ";");

            if (bracketOrSemicolonOrColon === "[") {
                // Array

                this.expect(";");
            }
            else if (bracketOrSemicolonOrColon === ";") {
                // Next field
            }

            union.addField(type, fieldName);

            const next = this.peekToken();

            if (next === "}") {
                break;
            }
        }

        this.expect("}");

        return union;
    }

    private parseType(name: string) {
        if (name === "struct") {
            return this.parseStruct();
        }
        else if (name === "union") {
            return this.parseUnion();
        }
        else {
            return this.data.getType(name);
        }
    }

    private oneOf<T extends string[]>(...tokens: [...T]): T[number] {
        const next = this.readToken();

        if (!next || tokens.indexOf(next) < 0) {
            throw new CParserError(`Expected one of: '${tokens}'`, this.line, this.column);
        }

        return next;
    }

    private expect(token?: string) {
        const next = this.readToken();

        if (token !== undefined && (!next || next !== token)) {
            throw new CParserError(`Expected: '${token}'`, this.line, this.column);
        }

        if (next === null) {
            throw new CParserError(`Unexpected end of text`, this.line, this.column);
        }

        return next;
    }

    private readToken(returnNewLine = false) {
        let token = "";

        while (true) {
            if (this.position >= this.text.length) {
                if (token) {
                    return token;
                }

                return null;
            }

            const c = this.read();

            if (!c) {
                return null;
            }

            if (returnNewLine && c === "\n") {
                return "\n";
            }

            if (isWhiteSpace(c)) {
                if (token) {
                    break;
                }
                else {
                    continue;
                }
            }

            if (isIdentifierChar(c)) {
                token += c;

                while(isIdentifierCharInner(this.peek())) {
                    const c = this.read();
                    token += c;
                }

                return token;
            }

            if (isNumber(c)) {
                token += c;

                while(isNumber(this.peek())) {
                    const c = this.read();
                    token += c;
                }

                return token;
            }

            if (c === "/" && (this.peek() === "/" || this.peek() === "*")) {
                const c = this.read();

                if (c === "/") {
                    while (this.peek() && this.peek() !== "\n") {
                        this.read();
                    }
                }
                else if (c === "*") {
                    let star = false;

                    while (true) {
                        let c = this.read();

                        if (!c) {
                            break;
                        }

                        if (c === "/" && star) {
                            break;
                        }

                        if (c === "*") {
                            star = true;
                        }
                        else {
                            star = false;
                        }
                    }
                }

                continue;
            }

            return c;
        }

        return token;
    }

    private peekToken() {
        const position = this.position;
        const column = this.column;
        const line = this.line;

        const token = this.readToken();

        this.position = position;
        this.column = column;
        this.line = line;

        return token;
    }

    private peek() {
        return this.text[this.position + 1];
    }

    private read() {
        this.position++;
        this.column++;

        const c = this.text[this.position];

        if (c === "\n") {
            this.line++;
            this.column = 0;
        }

        return c;
    }
}

abstract class CType {
    constructor(
        readonly name: string
    ) {

    }

    abstract get bitSize(): number;

    makeArrayType(length: number) {
        return new CArrayType(this, length);
    }

    abstract read(buffer: ArrayBuffer, offset: number, bitOffset: number): any;
}

class CStruct extends CType {
    private fields: { [key: string]: CType } = {};

    get bitSize() {
        return Object.values(this.fields).reduce((prev, type) => prev + type.bitSize, 0);
    }

    constructor(
        name: string
    ) {
        super(name)
    }

    read(buffer: ArrayBuffer, offset: number) {
        const result: { [key: string]: any } = {};
        let bitOffset = offset * 8;

        for (const prop in this.fields) {
            const field = this.fields[prop];

            result[prop] = field.read(buffer, Math.floor(bitOffset / 8), bitOffset % 8);

            bitOffset += field.bitSize;
        }

        if (bitOffset % 8 !== 0) {
            throw new Error("Misaligned");
        }

        return result;
    }

    addField(type: CType, fieldName: string) {
        this.fields[fieldName] = type;
    }
}

class CUnion extends CType {
    private fields: { [key: string]: CType } = {};

    get bitSize() {
        return Object.values(this.fields).reduce((prev, type) => type.bitSize > prev ? type.bitSize : prev, 0);
    }

    constructor(
        name: string
    ) {
        super(name)
    }

    read(buffer: ArrayBuffer, offset: number) {
        const result: { [key: string]: any } = {};
        let bitOffset = offset * 8;

        for (const prop in this.fields) {
            const field = this.fields[prop];

            result[prop] = field.read(buffer, Math.floor(bitOffset / 8), bitOffset % 8);
        }

        if (bitOffset % 8 !== 0) {
            throw new Error("Misaligned");
        }

        return result;
    }

    addField(type: CType, fieldName: string) {
        this.fields[fieldName] = type;
    }
}

export class CParserData {
    private types: { [key: string]: CType } = {
        char: new CTypeFixed("int8_t"),
        "unsigned char": new CTypeFixed("uint8_t"),
        short: new CTypeFixed("int16_t"),
        "unisgned short": new CTypeFixed("uint16_t"),
        int: new CTypeFixed("int32_t"),
        "unsigned int": new CTypeFixed("uint32_t"),
        int8_t: new CTypeFixed("int8_t"),
        uint8_t: new CTypeFixed("uint8_t"),
        int16_t: new CTypeFixed("int16_t"),
        uint16_t: new CTypeFixed("uint16_t"),
        int32_t: new CTypeFixed("int32_t"),
        uint32_t: new CTypeFixed("uint32_t"),
        bool: new CTypeFixed("bool"),
        float: new CTypeFixed("float"),
        double: new CTypeFixed("double"),
    }

    addType(name: string, type: CType) {
        this.types[name] = type;
    }

    getType(name: string) {
        return this.types[name];
    }

    getTypes() : Readonly<{ [key: string]: CType }> {
        return this.types;
    }
}

const cTypeFixedBitSizeMap = {
    int8_t: 8,
    uint8_t: 8,
    int16_t: 16,
    uint16_t: 16,
    int32_t: 32,
    uint32_t: 32,
    bool: 8,
    float: 32,
    double: 64
}

class CTypeFixed extends CType {
    private _bitSize: number;

    get bitSize() {
        return this._bitSize;
    }

    constructor(
        readonly name: "bool" | "uint8_t" | "int8_t" | "uint16_t" | "int16_t" | "uint32_t" | "int32_t" | "float" | "double",
        bitSize: number = cTypeFixedBitSizeMap[name]
    ) {
        super(name);

        this._bitSize = bitSize;
    }

    isUnsigned() {
        return this.name === "bool" ||
            this.name === "uint8_t" ||
            this.name === "uint16_t" ||
            this.name === "uint32_t";
    }

    isInteger() {
        return this.name === "bool" ||
            this.name === "uint8_t" ||
            this.name === "uint16_t" ||
            this.name === "uint32_t" ||
            this.name === "int8_t" ||
            this.name === "int16_t" ||
            this.name === "int32_t";
    }

    makeBitFieldType(bits: number) {
        return new CTypeFixed(this.name, bits);
    }

    read(buffer: ArrayBuffer, offset: number, bitOffset: number) {
        if ((this.bitSize === 8 || this.bitSize === 16 || this.bitSize === 32)  && bitOffset === 0) {
            const bytes = this.bitSize / 8;

            if (this.isInteger()) {
                if (this.isUnsigned()) {
                    if (bytes === 1) {
                        return new Uint8Array(buffer, offset, 1)[0];
                    }
                    else if (bytes === 2) {
                        return new Uint16Array(buffer.slice(offset, offset + bytes), 0, 1)[0];
                    }
                    else if (bytes === 4) {
                        return new Uint32Array(buffer.slice(offset, offset + bytes), 0, 1)[0];
                    }
                }
                else {
                    if (bytes === 1) {
                        return new Int8Array(buffer, offset, 1)[0];
                    }
                    else if (bytes === 2) {
                        return new Int16Array(buffer.slice(offset, offset + bytes), 0, 1)[0];
                    }
                    else if (bytes === 4) {
                        return new Int32Array(buffer.slice(offset, offset + bytes), 0, 1)[0];
                    }
                }
            }
            else {
                if (bytes === 4) {
                    return new Float32Array(buffer.slice(offset, offset + bytes), 0, 1)[0];
                }
                else if (bytes === 8) {
                    return new Float64Array(buffer.slice(offset, offset + bytes), 0, 1)[0];
                }
            }

            throw new Error(`Don't know how to read ${this.name}`);
        }

        const startByte = Math.floor((offset * 8 + bitOffset) / 8);
        const endByte = Math.floor((offset * 8 + bitOffset + this.bitSize) / 8);

        const addOne = (offset * 8 + bitOffset + this.bitSize) % 8 === 0 ? 0 : 1

        return this.getBits(new Uint8Array(buffer, startByte, endByte - startByte + addOne), bitOffset, this.bitSize);

    }

    private getBits(data: Uint8Array, offset: number, size: number) {
        let bits = "";

        for (let i = 0; i < data.length; i++) {
            bits = data[i].toString(2).padStart(8, "0") + bits;
        }

        return parseInt(bits.substr(bits.length - size - offset, size), 2);
    }
}

class CArrayType extends CType {
    get bitSize() {
        return this.elementType.bitSize * this.length;
    }

    constructor(
        readonly elementType: CType,
        readonly length: number
    ) {
        super(elementType.name + "[]");
    }

    read(buffer: ArrayBuffer, offset: number) {
        const result = [];

        for (let i = 0; i < this.length; i++) {
            result.push(this.elementType.read(buffer, offset + Math.floor((this.elementType.bitSize * i) / 8), 0));
        }

        return result;
    }
}